import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React, { FC, useMemo } from 'react'
import { batch } from 'react-redux'

import ImageByWidthDevice from '@/components/ImageByWidthDevice'
import {
  pressReleaseSetIsEventFilter,
  pressReleaseSetSelected,
  pressReleaseSetSkip,
  pressReleaseSetTags,
} from '@/reducers/press-release'
import { getNewsHeader } from '@/services/news'
import { getTags } from '@/services/tags'
import { TagListRequest } from '@/types/tags'
import clsxm from '@/utils/clsxm'
import { useAppDispatch, useAppSelector } from '@/utils/hooks'

import SearchBar from './components/SearchBar'

interface Banner {
  section?: string
}

const Banner: FC<Banner> = () => {
  const dispatch = useAppDispatch()
  const tagsFilter = useAppSelector((state) => state.pressRelease.tags)
  const router = useRouter()
  const { t } = useTranslation()

  const { data: newsBanner } = useAppSelector(
    getNewsHeader.select({
      headers: { 'Accept-Language': String(router.locale) },
    })
  )

  const requestList: TagListRequest = useMemo(() => {
    return {
      params: { skip: 0, limit: 5 },
      headers: { 'Accept-Language': String(router.locale) },
    }
  }, [])

  const { data: tags } = useAppSelector(getTags.select(requestList))

  const handleResetSearch = (xid: string) => {
    let tagsFiltering = [...tagsFilter]
    if (!tagsFiltering.includes(xid)) {
      tagsFiltering = [xid]
    } else {
      tagsFiltering = []
    }

    batch(() => {
      dispatch(pressReleaseSetIsEventFilter(true))
      dispatch(pressReleaseSetSkip(0))
      dispatch(pressReleaseSetSelected(0))
      dispatch(pressReleaseSetTags(tagsFiltering))
    })
  }

  return (
    <div className='relative flex h-[207px] w-full items-center px-[30.5px] lg:h-[307px] lg:px-64 xl:px-[355px]'>
      <div className='absolute inset-0 z-0'>
        <div className='absolute inset-0 z-20 bg-black/20' />

        <div className='absolute inset-0 mix-blend-screen' />

        {newsBanner?.data.imageFiles && (
          <ImageByWidthDevice
            images={newsBanner?.data.imageFiles}
            alt='background image'
            className='h-full w-full object-cover object-center'
          />
        )}
      </div>

      <div className='container relative z-20 flex flex-col items-center justify-center px-0 text-white'>
        <h1 className='mb-4 whitespace-nowrap text-lg font-bold lg:text-[28px] lg:leading-[36px]'>
          {t('press:headline')}
        </h1>

        <SearchBar />

        <div className='mt-2 flex gap-4 self-start whitespace-nowrap'>
          <p className='text-xs leading-[18px]'>Popular Tags:</p>

          {tags?.data.items ? (
            <div className='flex flex-wrap gap-x-4 gap-y-1'>
              {tags?.data.items.map((item) => (
                <button
                  className={clsxm(
                    'relative cursor-pointer text-xs font-bold leading-[18px] after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-0 after:bg-white after:transition-all after:duration-300 hover:after:w-full',
                    tagsFilter.includes(item.name) && 'after:w-full'
                  )}
                  key={item.xid}
                  onClick={() => handleResetSearch(item.name)}
                >
                  {item.name}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default Banner
